import { Popper, Paper, Fade, ClickAwayListener } from '@mui/material';
import { styled as muiStyled, css } from '@mui/material/styles';
import React from 'react';

import { LEFT_NAV_EXPANDED_WIDTH } from './_type';

import {
  DesignToken,
  type DesignTokenSet,
} from '~/client/components/base/core';
import { spc } from '~/client/components/base/spacer';
import { useThemeMode } from '~/client/contexts/theme-context';
import { useDesignTokens } from '~/client/hooks/use-design-tokens';

interface Props {
  className?: string;
  /**
   * Anchor element to position the flyout next to
   */
  anchorEl: HTMLElement | null;
  /**
   * Whether the flyout is open
   */
  open: boolean;
  /**
   * Callback when the flyout should close
   */
  onClose: () => void;
  /**
   * Menu items to display in the flyout
   */
  children: React.ReactNode;
  /**
   * Title to show at the top of the flyout
   */
  title?: React.ReactNode;
}

const LeftNavFlyoutMenuImpl: React.FC<Props> = ({
  className,
  anchorEl,
  open,
  onClose,
  children,
  title,
}) => {
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      onClose();
    }
  };

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="right-start"
      transition
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [0, 8], // 8px gap from the sidebar
          },
        },
        {
          name: 'preventOverflow',
          options: {
            padding: 16,
          },
        },
      ]}
      style={{ zIndex: 1300 }}
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps} timeout={200}>
          <Paper className={className} elevation={8}>
            <ClickAwayListener onClickAway={onClose}>
              <div
                className="flyout-content"
                onKeyDown={handleKeyDown}
                role="menu"
                tabIndex={-1}
              >
                {title && <div className="flyout-title">{title}</div>}
                <div className="flyout-items">{children}</div>
              </div>
            </ClickAwayListener>
          </Paper>
        </Fade>
      )}
    </Popper>
  );
};

const StyledLeftNavFlyoutMenu = muiStyled(LeftNavFlyoutMenuImpl, {
  shouldForwardProp: (prop) => prop !== 'tokens' && prop !== 'isDark',
})<Props & { tokens?: DesignTokenSet; isDark?: boolean }>(({
  theme,
  tokens,
  isDark,
}) => {
  const t = tokens ?? DesignToken;
  const nav = t.nav as typeof t.nav & { sidebarBg?: string };
  const panelBg = isDark ? (nav.sidebarBg ?? t.surface1) : t.surface1;
  const panelBorder = isDark
    ? 'rgba(255,255,255,0.06)'
    : (theme.palette.divider ?? 'rgba(0,0,0,0.08)');
  const titleColor = isDark
    ? `var(--org-primary-contrast, ${t.nav.itemTextOnDark})`
    : t.textPrimary;
  return css`
    background-color: ${panelBg} !important;
    border-radius: ${t.radiusSmall}px;
    width: ${LEFT_NAV_EXPANDED_WIDTH}px;
    overflow: hidden;
    border: 1px solid ${panelBorder};

    .flyout-content {
      padding: ${theme.spacing(spc.xs)} 0;
    }

    .flyout-title {
      padding: ${theme.spacing(spc.s)} ${theme.spacing(spc.m)};
      padding-bottom: ${theme.spacing(spc.xs)};
      font-size: 14px;
      font-weight: 500;
      color: ${titleColor};

      /* Support both string titles and React element titles */
      > * {
        color: inherit;
      }
    }

    .flyout-items {
      display: flex;
      flex-direction: column;
      gap: ${theme.spacing(spc.xxs)};
    }
  `;
});

/**
 * Flyout menu that appears when clicking collapsed navigation items
 * with sub-items. Maintains the same width as the expanded sidebar (300px).
 */
export const LeftNavFlyoutMenu: React.FC<Props> = (props) => {
  const t = useDesignTokens();
  const { effectiveMode } = useThemeMode();
  const isDark = effectiveMode === 'dark';
  return <StyledLeftNavFlyoutMenu tokens={t} isDark={isDark} {...props} />;
};
